/**
 * links.eyecaptain.io — the bio link page on its own host.
 *
 * The same Netlify site answers on links.eyecaptain.io. There:
 *   - "/" is the link-in-bio page (netlify.toml serves it the SPA shell,
 *     app.html, instead of the prerendered home page; the app routes it to
 *     LinkInBio; the edge function writes its head);
 *   - "/links" goes to "/" and every other page to the same path on
 *     eyecaptain.io (301, query kept). Assets are not pages: the edge function
 *     never runs on them (excludedPath), so they load from either host.
 * The page exists only there: eyecaptain.io/links is an unknown path (404).
 *
 * Imported by the app and by Deno (Netlify edge): no path aliases, no runtime imports.
 */

export const MAIN_HOST = "eyecaptain.io";
export const MAIN_URL = `https://${MAIN_HOST}`;
export const LINKS_HOST = "links.eyecaptain.io";
export const LINKS_URL = `https://${LINKS_HOST}/`;

/** The old path of the page; on the links host it goes to "/". */
const LINKS_PATH = "/links";

/**
 * Netlify's own paths. The Prerender extension sends crawler-like visitors
 * (Instagram's and Facebook's in-app browsers among them) to
 * /netlify-prerender-function on the same host with a token; sending that to
 * eyecaptain.io drops the token ("Authentication token is missing").
 */
const PLATFORM_PATHS = ["/netlify-prerender-function", "/.netlify/"];

/**
 * Hosts where the links page can be tried without the real host, by adding
 * ?links_host=1: deploy previews and branch deploys of the Netlify site, the
 * staging domain and a local dev server. Never eyecaptain.io itself.
 */
export function isPreviewHost(hostname: string): boolean {
  return (
    hostname.endsWith("--dreamy-dusk-6e4811.netlify.app") ||
    hostname === "test.eyecaptain.io" ||
    hostname === "localhost" ||
    hostname === "127.0.0.1"
  );
}

/** Is this visit the links host (or a preview pretending to be it with ?links_host=1)? */
export function isLinksHostMode(hostname: string, search = ""): boolean {
  if (hostname === LINKS_HOST) return true;
  return isPreviewHost(hostname) && new URLSearchParams(search).get("links_host") === "1";
}

/**
 * The 301 a request needs because of the links host, or null.
 *   links.eyecaptain.io/            → none (the page)
 *   links.eyecaptain.io/links?x     → https://links.eyecaptain.io/?x
 *   links.eyecaptain.io/pricing?x   → https://eyecaptain.io/pricing?x
 *   links.eyecaptain.io/netlify-prerender-function?url=… → none (Netlify's own)
 *   anything on another host        → none (eyecaptain.io/links is a 404 there)
 */
export function linksHostRedirect(url: URL): string | null {
  const path = url.pathname.replace(/\/+$/, "") || "/";
  if (url.hostname === LINKS_HOST) {
    if (path === "/") return null;
    if (PLATFORM_PATHS.some((p) => url.pathname.startsWith(p))) return null;
    if (path === LINKS_PATH) return `${LINKS_URL}${url.search}`;
    return `${MAIN_URL}${url.pathname}${url.search}`;
  }
  return null;
}

/** On the links host, a site path becomes an absolute eyecaptain.io address; elsewhere it stays as it is. */
export function siteHref(href: string, onLinksHost: boolean): string {
  if (!onLinksHost || !href.startsWith("/") || href.startsWith("//")) return href;
  return `${MAIN_URL}${href}`;
}
